import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { Price, PriceError, Steps, UpdatedPrice } from './types';

export type ppcDetailsProps = {
  canConvertToPPC: boolean;
  visibility: string;
  subscriptionMonthlyPrice: number | null;
  subscriptionAnnualPrice: number | null;
};

export type PpcDetails = {
  label: string;
  type: 'action' | 'text';
};

export const getPPCDetails = (
  props: ppcDetailsProps,
): PpcDetails[] | undefined => {
  if (props.visibility === 'exclusive') {
    const priceInfo: PpcDetails[] = [];
    if (props.subscriptionMonthlyPrice) {
      priceInfo.push({
        type: 'text',
        label: `Monthly subscription price is set to ${
          props.subscriptionMonthlyPrice / 100
        } USD/Month`,
      });
    }
    if (props.subscriptionAnnualPrice) {
      priceInfo.push({
        type: 'text',
        label: `Annual subscription price is set to ${
          props.subscriptionAnnualPrice / 100
        } USD/Year`,
      });
    }
    if (props.subscriptionMonthlyPrice === null) {
      priceInfo.push({ type: 'action', label: 'Offer a monthly plan' });
    }
    if (props.subscriptionAnnualPrice === null) {
      priceInfo.push({ type: 'action', label: 'Offer an annual plan' });
    }
    return priceInfo;
  }

  if (!props.canConvertToPPC) {
    return [
      {
        type: 'text',
        label:
          'This creator cannot be converted into a PPC. Only creators in onboaring mode could be converted.',
      },
    ];
  }
};

export type getStepsProps = {
  close: () => void;
  save: () => void;
  proceed: () => void;
  updatedPrice: UpdatedPrice;
  changePrice: (p: { key: UpdatedPrice; value: number }) => void;
};

export const getSteps = ({
  close,
  save,
  proceed,
  updatedPrice,
  changePrice,
}: getStepsProps): Steps => ({
  start: {
    icon: faTriangleExclamation,
    title: 'Are you sure you want to make this creator PPC?',
    description:
      'The creator will no longer be available to platform users. Please note that this action cannot be undone. Proceed with caution.',
    cta: [
      {
        title: 'Cancel',
        onClick: close,
        className: `text-primary`,
      },
      {
        title: 'Proceed',
        onClick: proceed,
        className: `bg-primary text-white`,
      },
    ],
  },
  input: {
    title: 'Please enter subscription price',
    description:
      'Please keep in mind that the subscription price you enter is final and you won’t be able to change it later. <br/>You can add a Monthly or Annual subscription or both.',
    priceForm: [
      {
        key: 'monthly',
        checkboxLabel: 'Monthly Subscription',
        inputLabel: 'Monthly Subscription Price (USD)',
        onChange: (value: number) => changePrice({ key: 'monthly', value }),
      },
      {
        key: 'annual',
        checkboxLabel: 'Annual Subscription',
        inputLabel: 'Annual Subscription Price (USD)',
        onChange: (value: number) => changePrice({ key: 'annual', value }),
      },
    ],
    cta: [
      {
        title: 'Cancel',
        onClick: close,
        className: `text-primary`,
      },
      {
        title: 'Save',
        onClick: save,
        className: `bg-primary text-white`,
        hasLoadingState: true,
      },
    ],
  },
  update: {
    title: 'Please enter subscription price',
    description:
      'Please keep in mind that the subscription price you enter is final and you won’t be able to change it later.',
    priceInput:
      updatedPrice === 'monthly'
        ? {
            key: 'monthly',
            inputLabel: 'Monthly Subscription Price (USD)',
            onChange: (value: number) => changePrice({ key: 'monthly', value }),
          }
        : {
            key: 'annual',
            inputLabel: 'Annual Subscription Price (USD)',
            onChange: (value: number) => changePrice({ key: 'annual', value }),
          },
    cta: [
      {
        title: 'Cancel',
        onClick: close,
        className: `text-primary`,
      },
      {
        title: 'Save',
        onClick: save,
        className: `bg-primary text-white`,
        hasLoadingState: true,
      },
    ],
  },
});

export type ValidateInitialPriceParams = {
  price: Price;
  selectedType: { monthly: boolean; annual: boolean };
};

export const validateInitialPrice = ({
  price,
  selectedType,
}: ValidateInitialPriceParams) => {
  const errors: PriceError = {};

  if (!selectedType.monthly && !selectedType.annual) {
    return { general: 'Please select at least one fee type.' };
  }

  if (selectedType.monthly && (!price.monthly || price.monthly === 0)) {
    errors.monthly = 'Please add fee for selected monthly subscription.';
  }
  if (selectedType.monthly && price.monthly && price.monthly < 1) {
    errors.monthly = 'Monthly price should be at least 1 USD.';
  }

  if (selectedType.annual && (!price.annual || price.annual === 0)) {
    errors.annual = 'Please add fee for selected annual subscription.';
  }

  if (price.annual && price.monthly && price.annual <= price.monthly) {
    errors.annual = `Annual price must be higher than monthly price.`;
  }

  if (selectedType.annual && price.annual && price.annual < 1) {
    errors.annual = 'Annual price must be at least 1 USD.';
  }

  return errors;
};

export type ValidatePriceUpdateParams = {
  price: Price;
  updatedPrice: UpdatedPrice;
  subscriptionMonthlyPrice: number | null;
  subscriptionAnnualPrice: number | null;
};
export const validatePriceUpdate = ({
  price,
  updatedPrice,
  subscriptionAnnualPrice,
  subscriptionMonthlyPrice,
}: ValidatePriceUpdateParams) => {
  if (updatedPrice === 'monthly') {
    if (!price.monthly || price.monthly === 0) {
      return {
        monthly: 'Please set montly subscription price.',
      };
    }
    if (price.monthly && price.monthly < 1) {
      return { monthly: 'Monthly price should be at least 1 USD.' };
    }
    if (
      price?.monthly &&
      subscriptionAnnualPrice &&
      convertDollarsToCents(price.monthly) >= subscriptionAnnualPrice
    ) {
      return {
        monthly: 'Monthly price must be lower than annual price.',
      };
    }
  }

  if (updatedPrice === 'annual') {
    if (!price.annual || price.annual === 0) {
      return {
        annual: 'Please set annual subscription price.',
      };
    }

    if (price?.annual && price.annual < 1) {
      return {
        annual: 'Annual price must be at least 1 USD.',
      };
    }

    if (
      price?.annual &&
      subscriptionMonthlyPrice &&
      convertDollarsToCents(price.annual) <= subscriptionMonthlyPrice
    ) {
      return {
        annual: `Annual price must be higher than monthly price ${
          subscriptionMonthlyPrice / 100
        } USD.`,
      };
    }
  }
};

export const convertDollarsToCents = (price: number): number => {
  if (Number.isInteger(price)) {
    return price * 100;
  }
  const [int, dec] = price.toString().split('.');
  return parseInt(int) * 100 + parseInt(dec);
};

export const getVariablesFromPrice = (price: Price) => {
  const variables: { monthlyPriceCents?: number; annualPriceCents?: number } =
    {};
  if (price.monthly) {
    variables.monthlyPriceCents = convertDollarsToCents(price.monthly);
  }
  if (price.annual) {
    variables.annualPriceCents = convertDollarsToCents(price.annual);
  }
  return variables;
};

export const getToastMessages = (
  isExclusive: boolean,
  updatedPrice: UpdatedPrice,
) => {
  if (!isExclusive) {
    return {
      success: 'Creator converted successfully',
      error: 'Something went wrong during conversion to PPC',
    };
  }

  if (updatedPrice == 'monthly') {
    return {
      success: 'Monthly subscription has been successfully added.',
      error: 'Something went wrong with setting monthly subscription',
    };
  }

  return {
    success: 'Annual subscription has been successfully added.',
    error: 'Something went wrong with setting annual subscription',
  };
};
